import {AxiosError} from 'axios'
import {useMutation, useQuery} from 'react-query'

import {API} from '../../networking/http'
import {PromoApplied, PromoError} from '../promo-codes/types'

export type StoreCategory = {uid: string; name: string}

export type StoreCard = {
  uid: string
  name: string
  hasVariants: boolean
  categoryId: string | null
  thumbUrl: string | null
  priceMin: number | null
  priceMax: number | null
  compareAtPrice: number | null
  measureType: string | null
  measureAmount: number | null
  inStock: boolean
}

export type StoreSettings = {
  payOnLocation: boolean
}

export type StoreGoodsResponse = {
  items: StoreCard[]
  categories: StoreCategory[]
  // Venue-level store switches (WBA-4538); absent on an older server.
  settings?: StoreSettings
}

export type StoreOption = {name: string; values: string[]}

export type StoreVariant = {
  label: string
  optionValues: Record<string, string>
  price: number
  compareAtPrice: number | null
  imageIndex: number | null
  available: boolean
}

export type StoreProduct = {
  uid: string
  name: string
  description: string
  categoryId: string | null
  media: string[]
  thumbnail: number
  measureType: string | null
  measureAmount: number | null
  hasVariants: boolean
  inStock: boolean
  options?: StoreOption[]
  variants?: Record<string, StoreVariant>
  price?: number
  compareAtPrice?: number | null
}

export type StoreOrderLine = {
  quantity: number
  price: number
  label: string
  variantLabel: string | null
  stockItemId: string | null
}

export type StoreOrder = {
  uid: string
  reference: string | null
  venueId: string
  isPaid: boolean
  isCancelled: boolean
  orderStatus: 'CANCELLED' | 'COMPLETED' | 'PLACED' | 'PROCESSING' | 'READY_FOR_PICKUP'
  totalPrice: number
  createdAt: number
  statusTimestamps: Record<string, number>
  lines: StoreOrderLine[]
  // D364: lines keep their list price; the code comes off the total.
  promoCode?: string | null
  promoDiscount?: number | null
}

export type StoreCheckoutLine = {
  stockItemId: string
  variantId?: string
  quantity: number
}

export type StoreCheckoutPayload = {
  venueId: string
  lines: StoreCheckoutLine[]
  payOnLocation?: boolean
  userName?: string
  promoCode?: string
}

export type StoreCheckoutResponse = {
  entityId: string
  totalPrice: number
  reference?: string
  payOnLocation: boolean
  promoDiscount?: number
  promoApplied?: PromoApplied | null
}

export type StoreQuotePayload = {
  lines: StoreCheckoutLine[]
  promoCode?: string
}

// What the code does to one cart line, in the order the lines were sent (server #3633).
export type StoreQuoteLine = {
  stockItemId: string
  variantId?: string
  eligible: boolean
  promoDiscount: number
  payableAfter: number
}

export type StoreQuoteResponse = {
  lines?: StoreQuoteLine[]
  subtotal: number
  promoDiscount: number
  totalPrice: number
  currency: string
  promoApplied: PromoApplied | null
  promoError: PromoError | null
}

export const storeQuoteQueryKey = 'storeQuote'

// Server-priced cart (contract D364 §3.4). Only asked while a promo code is in play; without one
// the cart total is the sum of the lines, as before.
export const useStoreQuote = (venueId: string | undefined, body: StoreQuotePayload) =>
  useQuery<StoreQuoteResponse, AxiosError>(
    [storeQuoteQueryKey, venueId, body],
    () => API.post<StoreQuotePayload, StoreQuoteResponse>(`store/${venueId}/quote`, body).then((res) => res.data),
    {
      enabled: Boolean(venueId && body.promoCode && body.lines.length > 0),
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      cacheTime: 1000 * 30,
    },
  )

export const useStoreGoods = (venueId?: string) =>
  useQuery<StoreGoodsResponse, AxiosError>(
    ['storeGoods', venueId],
    () => API.get<undefined, StoreGoodsResponse>(`store/${venueId}/goods`).then((res) => res.data),
    {enabled: Boolean(venueId), refetchOnWindowFocus: false, cacheTime: 1000 * 60},
  )

export const useStoreProduct = (venueId?: string, stockItemId?: string) =>
  useQuery<StoreProduct, AxiosError>(
    ['storeProduct', venueId, stockItemId],
    () => API.get<undefined, StoreProduct>(`store/${venueId}/goods/${stockItemId}`).then((res) => res.data),
    {enabled: Boolean(venueId && stockItemId), refetchOnWindowFocus: false, cacheTime: 1000 * 60},
  )

export const useStoreOrders = (enabled: boolean) =>
  useQuery<StoreOrder[], AxiosError>(
    ['storeOrders'],
    () => API.get<undefined, StoreOrder[]>('store/orders').then((res) => res.data),
    {enabled, refetchOnWindowFocus: false, cacheTime: 1000 * 30},
  )

export const useStoreOrder = (saleId?: string) =>
  useQuery<StoreOrder, AxiosError>(
    ['storeOrder', saleId],
    () => API.get<undefined, StoreOrder>(`store/orders/${saleId}`).then((res) => res.data),
    {
      enabled: Boolean(saleId),
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 30,
      // A paid checkout redirects here the moment the charge succeeds; the
      // webhook that promotes the draft into the order can be a few seconds
      // behind. Retrying bridges that gap instead of showing an empty order
      // (Dima 2026-08-19).
      retry: 8,
      retryDelay: () => 2000,
    },
  )

export const useStoreCheckout = () =>
  useMutation<
    StoreCheckoutResponse,
    AxiosError<{type?: string; message?: string; details?: unknown[]}>,
    StoreCheckoutPayload
  >(({venueId, ...body}) =>
    API.post<StoreCheckoutPayload, StoreCheckoutResponse>(`store/${venueId}/checkout`, body).then((res) => res.data),
  )
